import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  UploadContainer,
  UploadButton,
  FileInfoCard,
  PRIMARY_COLOR,
  NEUTRAL_800,
  SUCCESS_COLOR,
} from "./styles";
import { useAuthHeaders } from "../../hooks/useAuthHeaders";
import {
  initFigmaUpload,
  uploadToS3,
  formatFileSize,
} from "./figmaUploadUtils";
import { adminApiService } from "../../utils/api";
import { GfFigmaTemplateFile } from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../locales/messages";

interface Props {
  value?: GfFigmaTemplateFile;
  onChange: (file?: GfFigmaTemplateFile) => void;
  disabled?: boolean;
  onUploadingChange?: (uploading: boolean) => void;
}

const MAX_SIZE = 2 * 1024 * 1024 * 1024; // 2GB

export const FigmaFileUploadSection: React.FC<Props> = ({
  value,
  onChange,
  disabled,
  onUploadingChange,
}) => {
  const { t } = useTranslation();
  const [fileName, setFileName] = useState<string>("");
  const [error, setError] = useState<string>();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { headers } = useAuthHeaders();

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    await processFile(file);
  };

  const handleButtonClick = () => {
    if (disabled || isUploading) return;
    fileInputRef.current?.click();
  };

  const processFile = async (file: File) => {
    setError(undefined);
    setUploadSuccess(false);

    if (file.size > MAX_SIZE) {
      const errorMsg = t(messages.File_Size_Exceeds_2Gb_Limit(), {
        fileSize: formatFileSize(file.size),
      });
      console.error("File size error:", errorMsg);
      setError(errorMsg);
      return;
    }

    const allowedExtensions = [".fig", ".zip"];
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      const errorMsg = t(messages.File_Type_Not_Supported(), {
        allowedTypes: allowedExtensions.join(", "),
      });
      console.error("File type error:", errorMsg);
      setError(errorMsg);
      return;
    }

    const token = headers.Authorization?.replace("Bearer ", "");
    if (!token) {
      const errorMsg = t(messages.Authentication_Required());
      setError(errorMsg);
      return;
    }

    setFileName(file.name);
    setIsUploading(true);
    onUploadingChange?.(true);

    try {
      const initResp = await initFigmaUpload(file, token);

      await uploadToS3(file, initResp);

      const created =
        await adminApiService.createOneBaseGfFigmaTemplateFileAdminControllerGfFigmaTemplateFile(
          {
            src: (initResp as any).publicUrl,
            originalFileName: file.name,
            fileSize: file.size,
          } as GfFigmaTemplateFile,
          { headers }
        );

      setUploadSuccess(true);
      onChange(created as GfFigmaTemplateFile);
    } catch (err: any) {
      console.error("Upload error:", err);
      const errorMsg =
        err?.message || err?.toString() || t(messages.Upload_Failed());
      setError(errorMsg);
      setUploadSuccess(false);
    } finally {
      setIsUploading(false);
      onUploadingChange?.(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isUploading) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled || isUploading) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const file = files[0];
    await processFile(file);
  };

  const getContainerClass = () => {
    if (isUploading) return "uploading";
    if (uploadSuccess || value) return "success";
    if (error) return "error";
    if (isDragOver) return "dragover";
    return "";
  };

  const hasFile = !!value || !!fileName;

  return (
    <Box data-testid="figma-file-upload-section">
      <Stack spacing={2}>
        <UploadContainer
          className={getContainerClass()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          data-testid="figma-file-upload-section-drop-zone"
        >
          {hasFile ? (
            <Stack alignItems="center" spacing={2} width="100%">
              <InsertDriveFileIcon
                sx={{
                  fontSize: 48,
                  color: uploadSuccess ? SUCCESS_COLOR : PRIMARY_COLOR,
                  mb: 1,
                }}
              />

              {isDragOver && (
                <Typography
                  variant="h6"
                  fontWeight="600"
                  color={PRIMARY_COLOR}
                  textAlign="center"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 2,
                  }}
                >
                  {t(messages.Drop_To_Replace_File())}
                </Typography>
              )}

              <FileInfoCard
                sx={{
                  opacity: isDragOver ? 0.3 : 1,
                  transition: "opacity 0.2s",
                }}
                data-testid="figma-file-upload-section-file-info"
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="600"
                  color={NEUTRAL_800}
                  mb={1}
                >
                  {value?.originalFileName ||
                    fileName ||
                    t(messages.Uploaded_File())}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ wordBreak: "break-all" }}
                >
                  {value?.src || t(messages.Processing())}
                </Typography>

                {uploadSuccess && (
                  <Chip
                    label={t(messages.Upload_Complete())}
                    color="success"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </FileInfoCard>

              {isUploading && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  mt={2}
                  data-testid="figma-file-upload-section-loader"
                >
                  <CircularProgress size={20} />
                  <Typography variant="caption" color="text.secondary">
                    {t(messages.Uploading_File())}
                  </Typography>
                </Box>
              )}

              <Box
                sx={{
                  display: "inline-flex",
                  opacity: isDragOver ? 0.3 : 1,
                  transition: "opacity 0.2s",
                }}
              >
                <UploadButton
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  disabled={isUploading || disabled}
                  onClick={handleButtonClick}
                  data-testid="figma-file-upload-section-replace-button"
                  sx={{
                    background: "rgba(255, 255, 255, 0.9)",
                    color: PRIMARY_COLOR,
                    border: `2px solid ${PRIMARY_COLOR}40`,
                    cursor: isUploading || disabled ? "not-allowed" : "pointer",
                    "&:hover": {
                      background: `${PRIMARY_COLOR}08`,
                      borderColor: PRIMARY_COLOR,
                    },
                  }}
                >
                  {t(messages.Replace_File())}
                </UploadButton>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept=".fig,.zip"
                  onChange={handleFile}
                  data-testid="figma-file-upload-section-replace-input"
                />
              </Box>
            </Stack>
          ) : (
            <>
              <CloudUploadIcon
                sx={{
                  fontSize: 56,
                  color: `${PRIMARY_COLOR}60`,
                  mb: 1,
                }}
              />
              <Typography
                variant="h6"
                fontWeight="600"
                color={NEUTRAL_800}
                textAlign="center"
              >
                {isDragOver
                  ? t(messages.Drop_Your_File_Here())
                  : t(messages.Drop_Your_Figma_File_Here())}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                mb={2}
              >
                {isDragOver
                  ? t(messages.Release_To_Upload_Your_File())
                  : t(messages.Optional_Drag_And_Drop_Or_Click_To_Select())}
              </Typography>

              <Box
                sx={{
                  display: "inline-flex",
                }}
              >
                <UploadButton
                  startIcon={<UploadFileIcon />}
                  disabled={isUploading || disabled}
                  onClick={handleButtonClick}
                  data-testid="figma-file-upload-section-select-button"
                  sx={{
                    cursor: isUploading || disabled ? "not-allowed" : "pointer",
                  }}
                >
                  {isUploading
                    ? t(messages.Uploading())
                    : isDragOver
                    ? t(messages.Drop_Here())
                    : t(messages.Select_File())}
                </UploadButton>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept=".fig,.zip"
                  onChange={handleFile}
                  data-testid="figma-file-upload-section-select-input"
                />
              </Box>

              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
              >
                {t(messages.Maximum_File_Size_2Gb())}
              </Typography>
            </>
          )}
        </UploadContainer>

        {error && (
          <Alert
            severity="error"
            sx={{ borderRadius: "12px" }}
            onClose={() => setError(undefined)}
            data-testid="figma-file-upload-section-error-alert"
          >
            {error}
          </Alert>
        )}

        {uploadSuccess && !error && (
          <Alert
            severity="success"
            sx={{ borderRadius: "12px" }}
            data-testid="figma-file-upload-section-success-alert"
          >
            {t(messages.File_Uploaded_Successfully_S3_Url_Generated())}
          </Alert>
        )}
      </Stack>
    </Box>
  );
};

export default FigmaFileUploadSection;
